import React, { createContext, useCallback, useContext, useState } from "react";

import i18n from "../i18n";

// The language selector reloads the page on change, so translating the
// defaults once at initialisation is safe.
const defaultPageInfo = () => ({
  name: i18n.t("Dashboard", { ns: "navigation" }),
  desc: i18n.t("A quick glance at the current status of your business", { ns: "common" }),
  fill: false,
  nameSuffix: "",
});

interface PageNameContextInterface {
  pageInfo: {
    name: string;
    desc: string;
    fill: boolean;
    // Extra detail shown alongside the title in smaller, highlighted text, so the
    // title itself stays recognisable.
    nameSuffix: string;
  };
  setInfo: React.Dispatch<
    React.SetStateAction<{
      name: string;
      desc: string;
      fill: boolean;
      nameSuffix: string;
    }>
  >;
}

interface PageNameProviderInterface {
  children: React.ReactNode;
}

const PageNameContext = createContext<PageNameContextInterface>({
  pageInfo: defaultPageInfo(),
  setInfo: () => { },
});

export function PageNameProvider({ children }: PageNameProviderInterface) {
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);

  return (
    <PageNameContext.Provider
      value={{ pageInfo: pageInfo, setInfo: setPageInfo }}
    >
      {children}
    </PageNameContext.Provider>
  );
}

export function usePageNameContext() {
  const pageNameCtx = useContext(PageNameContext);

  if (!pageNameCtx) {
    throw new Error(
      "usePageNameContext must be used within a PageNameProvider"
    );
  }

  const setInfo = useCallback(
    (info: { name: string; desc: string; fill?: boolean; nameSuffix?: string }) => {
      pageNameCtx.setInfo({ ...info, fill: info.fill ?? false, nameSuffix: info.nameSuffix ?? "" });
      document.title = `Wilma | ${info.name}`;
    },
    [pageNameCtx.setInfo]
  );

  return { ...pageNameCtx, setInfo };
}
